import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Inbox, Search, ChevronDown, ChevronUp, X, Sparkles, MapPin, CornerDownRight, GitBranch, RotateCcw, AlertTriangle } from "lucide-react";
import { renderSnippet } from "@/lib/searchSnippet";
import { ANY_LOCATION, LOCATION_FILTER_HINT, locationOptions, matchesLocation } from "@/lib/locationFilter";
import { matchesScope } from "@/lib/scope";
import { ScopeChip, ScopeFilterToggle } from "@/components/ScopeChip";
import { formatDuration } from "@/lib/speech";
import { ProposeEdit, OriginalTranscript } from "@/components/ProposeEdit";
import { Input } from "@/components/ui/input";
import { useCategories } from "@/context/CategoriesContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TRANSFORM_LABELS = {
  raw: "Alleen transcript",
  summary: "Samenvatting",
  bullets: "Bulletpoints",
  sop: "Werkinstructie (SOP)",
  article: "Kennisartikel",
};
const TRANSFORM_ORDER = ["summary", "bullets", "sop", "article"];

const ALL = "__ALL__";

function formatDayTime(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    if (sameDay(d, today)) return `Vandaag · ${time}`;
    if (sameDay(d, y)) return `Gisteren · ${time}`;
    return `${d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} · ${time}`;
  } catch {
    return iso;
  }
}

function deriveTitle(r) {
  if (r.auto_title && r.auto_title.trim()) return r.auto_title.trim();
  if (r.question && r.question.trim()) return r.question.trim();
  const words = ((r.display_text || r.transcript) || "").split(/\s+/).filter(Boolean).slice(0, 8);
  return words.join(" ") + (words.length === 8 ? "…" : "");
}

function excerpt(text, max = 160) {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return t.slice(0, max).replace(/\s+\S*$/, "") + "…";
}

function buildThread(items, id) {
  const byId = new Map(items.map((r) => [r.id, r]));
  const start = byId.get(id);
  if (!start) return [];
  const seen = new Set([id]);
  const ancestors = [];
  let cur = start;
  while (cur.parent_id && byId.has(cur.parent_id) && !seen.has(cur.parent_id)) {
    const p = byId.get(cur.parent_id);
    ancestors.unshift(p);
    seen.add(p.id);
    cur = p;
  }
  const chain = [...ancestors, start];
  cur = start;
  while (true) {
    // FIX#4 — same rule as the server (_walk_thread): pick the OLDEST child by created_at,
    // never the newest, so Mijn kluis renders the exact same single branch as Bibliotheek.
    const siblings = items.filter((r) => r.parent_id === cur.id && !seen.has(r.id));
    if (siblings.length === 0) break;
    const child = siblings.reduce((oldest, r) => (r.created_at < oldest.created_at ? r : oldest), siblings[0]);
    chain.push(child);
    seen.add(child.id);
    cur = child;
  }
  return chain;
}

export default function SubmissionsPage() {
  const navigate = useNavigate();
  const { colorOf } = useCategories();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState({}); // id -> "processed" | "raw"
  const [processingId, setProcessingId] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL);
  const [tagFilter, setTagFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [onlyAll, setOnlyAll] = useState(false);

  // B3.2 — server full-text search takes over from the client filter at 2+ chars.
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchTokenRef = useRef(0); // FIX#8 (B-1) — discard a stale in-flight response
  const searching = query.trim().length >= 2;

  useEffect(() => {
    const q = query.trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (q.length < 2) {
      searchTokenRef.current += 1;
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      const myToken = ++searchTokenRef.current;
      try {
        const params = { q, scope: "mine", limit: 50 };
        if (category !== ALL) params.category = category;
        const { data } = await api.get("/search", { params });
        if (searchTokenRef.current !== myToken) return; // a newer search already superseded this one
        setSearchResults(data);
      } catch (e) {
        if (searchTokenRef.current !== myToken) return;
        setSearchResults([]);
        toast.error(formatApiError(e));
      } finally {
        if (searchTokenRef.current === myToken) setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [query, category]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recordings");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const categoriesInList = useMemo(() => {
    const s = new Set();
    for (const r of items) if (r.category) s.add(r.category);
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (category !== ALL && r.category !== category) return false;
      if (tagFilter && !(r.tags || []).includes(tagFilter)) return false;
      if (locationFilter && !matchesLocation(r, locationFilter)) return false;
      if (!matchesScope(r, onlyAll)) return false;
      if (!q) return true;
      const hay = (
        (r.display_text || "") + " " +
        (r.transcript || "") + " " +
        (r.question || "") + " " +
        (r.auto_title || "") + " " +
        ((r.tags || []).join(" ")) + " " +
        ((r.locations || []).join(" "))
      ).toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, category, tagFilter, locationFilter, onlyAll]);
  const setItemScope = (id, patch) => setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  // Search results carry only a slim shape (+ snippet) — rendered as a dedicated compact
  // list (FIX#6, B-1); opening one hydrates the FULL memo via GET /recordings/{id} and
  // expands it in the normal (non-search) list — a search hit is NEVER silently dropped
  // just because it's outside the loaded `limit=200` page, unlike the old enrich-in-place
  // approach that quietly filtered results against `items`.
  const openOwnMemo = async (id) => {
    setQuery("");
    setCategory(ALL);
    setTagFilter(null);
    setLocationFilter(null);
    setOnlyAll(false);
    if (!items.some((i) => i.id === id)) {
      try {
        const { data } = await api.get(`/recordings/${id}`);
        setItems((prev) => (prev.some((i) => i.id === data.id) ? prev : [data, ...prev]));
      } catch {
        toast.error("Deze memo is niet (meer) beschikbaar.");
        return;
      }
    }
    setExpandedId(id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="submission-item-${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const toggleTagFilter = (t) => setTagFilter((cur) => (cur === t ? null : t));
  const toggleLocationFilter = (loc) => setLocationFilter((cur) => (cur === loc ? null : loc));
  const locationChoices = useMemo(() => locationOptions(items), [items]);

  const handleDelete = async (id) => {
    if (!window.confirm("Deze opname verwijderen? Dit kan niet ongedaan worden.")) return;
    setDeletingId(id);
    try {
      await api.delete(`/recordings/${id}`);
      setItems((prev) => prev.filter((r) => r.id !== id));
      toast.success("Opname verwijderd.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const handleProcess = async (id, transform_type) => {
    setProcessingId(id);
    try {
      const { data } = await api.post(`/recordings/${id}/process`, { transform_type });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      setViewMode((v) => ({ ...v, [id]: "processed" }));
      toast.success("Verwerkt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessingId(null);
    }
  };

  const filtersActive = query.trim().length > 0 || category !== ALL || !!tagFilter || !!locationFilter || onlyAll;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6 sm:py-14" data-testid="submissions-page">
      {/* Header */}
      <header className="mb-6 sm:mb-8 pb-6 border-b border-tw flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase">
            Persoonlijk en doorzoekbaar
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-5xl font-black text-white leading-none tracking-tight">
            Mijn <span className="text-tw-yellow">kluis.</span>
          </h1>
          <p className="mt-4 text-sm text-tw-muted max-w-md leading-relaxed">
            Alle inzichten die jij hebt vastgelegd, logisch geordend en direct terug te vinden.
          </p>
        </div>
        <div className="shrink-0 font-mono text-sm text-tw-yellow" data-testid="submissions-count">
          {searching ? (searchResults || []).length : filtered.length}
          <span className="text-tw-muted"> / {items.length}</span>
        </div>
      </header>

      {/* Filter row: search + category + locatie */}
      <div className="mb-2 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-tw-muted pointer-events-none"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek in je memo's…"
            data-testid="submissions-search"
            className="pl-9 pr-9 bg-tw-surface border-tw text-white h-11 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Zoekopdracht wissen"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-tw-muted hover:text-tw-yellow"
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger
            data-testid="submissions-category-filter"
            className="tw-press bg-tw-surface border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow sm:w-56"
          >
            <SelectValue placeholder="Alle categorieën" />
          </SelectTrigger>
          <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
            <SelectItem value={ALL} className="focus:bg-tw-yellow focus:text-black rounded-none">
              Alle categorieën
            </SelectItem>
            {categoriesInList.map((c) => (
              <SelectItem key={c} value={c} data-testid={`submissions-cat-opt-${c}`} className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider">
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locationFilter || ANY_LOCATION} onValueChange={(v) => setLocationFilter(v === ANY_LOCATION ? null : v)}>
          <SelectTrigger
            data-testid="submissions-location-filter"
            className="tw-press bg-tw-surface border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow sm:w-48"
          >
            <SelectValue placeholder="Alle locaties" />
          </SelectTrigger>
          <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
            <SelectItem value={ANY_LOCATION} className="focus:bg-tw-yellow focus:text-black rounded-none">
              Alle locaties
            </SelectItem>
            {locationChoices.map((loc) => (
              <SelectItem key={loc} value={loc} data-testid={`submissions-loc-opt-${loc}`} className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider">
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <ScopeFilterToggle active={onlyAll} onToggle={() => setOnlyAll((v) => !v)} testId="submissions-scope-filter" />
        <p className="text-[11px] text-tw-muted" data-testid="submissions-location-hint">{LOCATION_FILTER_HINT}</p>
      </div>

      {/* Active chip-filter indicator */}
      {(tagFilter || locationFilter) && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="submissions-active-chip-filters">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-tw-muted">Filter:</span>
          {locationFilter && (
            <button
              type="button"
              onClick={() => setLocationFilter(null)}
              data-testid="submissions-clear-location-filter"
              className="tw-press inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-black bg-tw-yellow hover:bg-[#FFDE33]"
            >
              <MapPin size={11} strokeWidth={2.5} /> {locationFilter}
              <X size={11} strokeWidth={2.8} />
            </button>
          )}
          {tagFilter && (
            <button
              type="button"
              onClick={() => setTagFilter(null)}
              data-testid="submissions-clear-tag-filter"
              className="tw-press inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-black bg-tw-yellow hover:bg-[#FFDE33]"
            >
              #{tagFilter}
              <X size={11} strokeWidth={2.8} />
            </button>
          )}
        </div>
      )}

      {searching && (
        <p className="mb-4 font-mono text-[11px] text-tw-muted uppercase tracking-[0.15em]" data-testid="submissions-search-status">
          {searchLoading ? "Zoeken…" : `${(searchResults || []).length} resulta${(searchResults || []).length === 1 ? "at" : "ten"} voor "${query.trim()}"`}
        </p>
      )}

      {searching ? (
        searchLoading ? (
          <div className="flex items-center gap-3 text-tw-muted font-mono text-xs tracking-widest py-16 justify-center">
            <Loader2 className="animate-spin" size={16} /> LADEN…
          </div>
        ) : (searchResults || []).length === 0 ? (
          <EmptyState label={`Geen resultaten voor "${query.trim()}"`} hint="Pas je zoekopdracht of categorie aan." testId="submissions-no-results" />
        ) : (
          <ul className="divide-y divide-[#262626]" data-testid="submissions-search-results">
            {searchResults.map((r) => (
              <li key={r.id} data-testid={`submission-search-result-${r.id}`} className="py-5 tw-fadeup">
                <button type="button" onClick={() => openOwnMemo(r.id)} className="w-full text-left tw-press">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="inline-block w-1.5 h-1.5" style={{ backgroundColor: colorOf(r.category) }} aria-hidden />
                    <span className="font-mono uppercase text-[11px] tracking-[0.24em] text-tw-muted">{r.category}</span>
                    <span className="font-mono text-[11px] text-tw-muted">·</span>
                    <span className="font-mono text-[11px] text-tw-muted">{formatDayTime(r.created_at)}</span>
                  </div>
                  <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-snug tracking-tight">{r.title}</h2>
                  <p className="mt-1.5 text-tw-muted text-sm leading-relaxed" data-testid={`submission-search-snippet-${r.id}`}>
                    {renderSnippet(r.snippet)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : loading ? (
        <div className="flex items-center gap-3 text-tw-muted font-mono text-xs tracking-widest py-16 justify-center">
          <Loader2 className="animate-spin" size={16} /> LADEN…
        </div>
      ) : items.length === 0 ? (
        <EmptyState label="Je kluis is leeg" hint="Ga naar Opnemen en leg je eerste memo vast." testId="submissions-empty" />
      ) : filtered.length === 0 ? (
        <EmptyState
          label="Geen resultaten"
          hint="Pas je zoekopdracht of categorie aan."
          testId="submissions-no-results"
        />
      ) : (
        <ul className="divide-y divide-[#262626]" data-testid="submissions-list">
          {filtered.map((r) => {
            const catColor = colorOf(r.category);
            const isOpen = expandedId === r.id;
            const parent = r.parent_id ? items.find((x) => x.id === r.parent_id) : null;
            const hasChildren = items.some((x) => x.parent_id === r.id);
            return (
              <li key={r.id} data-testid={`submission-item-${r.id}`} className="py-5 tw-fadeup">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-1.5 h-1.5" style={{ backgroundColor: catColor }} aria-hidden />
                  <span className="font-mono uppercase text-[11px] tracking-[0.24em] text-tw-muted" data-testid={`submission-category-${r.id}`}>
                    {r.category}
                  </span>
                  <span className="font-mono text-[11px] text-tw-muted">·</span>
                  <span className="font-mono text-[11px] text-tw-muted">{formatDayTime(r.created_at)}</span>
                  {r.user_location && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted border border-tw px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`submission-speaker-location-${r.id}`} title="Locatie waar deze memo bij hoort">
                      <MapPin size={9} strokeWidth={2.5} /> hoort bij {r.user_location}
                    </span>
                  )}
                  <ScopeChip item={r} canEdit onChange={(s) => setItemScope(r.id, s)} testId={`submission-scope-${r.id}`} />
                  {typeof r.duration_seconds === "number" && (
                    <>
                      <span className="font-mono text-[11px] text-tw-muted">·</span>
                      <span className="font-mono tabular-nums text-[11px] text-tw-muted">{formatDuration(r.duration_seconds)}</span>
                    </>
                  )}
                </div>

                <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-snug tracking-tight">
                  {deriveTitle(r)}
                </h2>

                {r.transcript_warning && (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1.5 border border-tw-yellow bg-tw-yellow/10 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-tw-yellow"
                    data-testid={`submission-transcript-warning-${r.id}`}
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} />
                    Let op: deze opname bevatte veel stilte — controleer het transcript.
                  </div>
                )}


                {(r.parent_id && parent) || hasChildren ? (
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                    {r.parent_id && parent && (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] text-tw-muted" data-testid={`submission-parent-${r.id}`}>
                        <CornerDownRight size={11} strokeWidth={2.5} className="text-tw-yellow" />
                        Vervolg op: <span className="text-white normal-case">{deriveTitle(parent)}</span>
                      </span>
                    )}
                    {hasChildren && (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] text-tw-yellow" data-testid={`submission-haschild-${r.id}`}>
                        <GitBranch size={11} strokeWidth={2.5} /> Heeft vervolg-memo
                      </span>
                    )}
                  </div>
                ) : null}

                {((r.tags && r.tags.length > 0) || (r.locations && r.locations.length > 0)) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`submission-chips-${r.id}`}>
                    {(r.locations || []).map((loc) => (
                      <button
                        type="button"
                        key={`loc-${loc}`}
                        onClick={() => toggleLocationFilter(loc)}
                        data-testid={`submission-location-${r.id}-${loc}`}
                        className={`tw-press inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                          locationFilter === loc
                            ? "text-black bg-tw-yellow border-tw-yellow"
                            : "text-tw-yellow border-tw-yellow/40 bg-tw-yellow/5 hover:bg-tw-yellow/15"
                        }`}
                      >
                        <MapPin size={10} strokeWidth={2.5} /> {loc}
                      </button>
                    ))}
                    {(r.tags || []).map((t) => (
                      <button
                        type="button"
                        key={`tag-${t}`}
                        onClick={() => toggleTagFilter(t)}
                        data-testid={`submission-tag-${r.id}-${t}`}
                        className={`tw-press inline-block px-2 py-0.5 font-mono text-[10px] tracking-[0.1em] border transition-colors ${
                          tagFilter === t
                            ? "text-black bg-tw-yellow border-tw-yellow"
                            : "text-tw-muted border-tw bg-tw-black hover:border-tw-yellow hover:text-tw-yellow"
                        }`}
                      >
                        #{t}
                      </button>
                    ))}
                  </div>
                )}

                {!isOpen && (
                  <p className="mt-2 text-sm text-tw-muted leading-relaxed">
                    {excerpt(r.display_text || r.transcript)}
                  </p>
                )}

                {isOpen && (
                  <div className="mt-3 pl-3 border-l-2 border-tw-yellow" data-testid={`submission-full-${r.id}`}>
                    {r.question && (
                      <div className="mb-2">
                        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">Kennisvraag</div>
                        <p className="mt-1 text-sm italic text-tw-muted leading-snug">&ldquo;{r.question}&rdquo;</p>
                      </div>
                    )}
                    {r.auto_summary && (
                      <div className="mb-3" data-testid={`submission-summary-${r.id}`}>
                        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">Samenvatting (AI)</div>
                        <p className="mt-1 text-sm text-white leading-relaxed">{r.auto_summary}</p>
                      </div>
                    )}
                    {r.processed_text && (viewMode[r.id] || "processed") === "processed" ? (
                      <>
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">
                            {TRANSFORM_LABELS[r.transform_type] || "Verwerkt"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setViewMode((v) => ({ ...v, [r.id]: "raw" }))}
                            data-testid={`submission-toggle-raw-${r.id}`}
                            className="tw-press font-mono text-[9px] tracking-[0.2em] uppercase text-tw-muted hover:text-tw-yellow"
                          >
                            Toon origineel transcript
                          </button>
                        </div>
                        <div className="text-white leading-relaxed whitespace-pre-wrap tw-reading" data-testid={`submission-processed-${r.id}`}>
                          {r.processed_text}
                        </div>
                      </>
                    ) : r.processed_text ? (
                      <>
                        <div className="mb-2">
                          <button
                            type="button"
                            onClick={() => setViewMode((v) => ({ ...v, [r.id]: "processed" }))}
                            data-testid={`submission-toggle-processed-${r.id}`}
                            className="tw-press font-mono text-[9px] tracking-[0.2em] uppercase text-tw-muted hover:text-tw-yellow"
                          >
                            Toon verwerkte versie
                          </button>
                        </div>
                        <p className="text-white leading-relaxed whitespace-pre-wrap tw-reading">{r.display_text || r.transcript}</p>
                        {r.display_text && <OriginalTranscript transcript={r.transcript} id={r.id} />}
                      </>
                    ) : (
                      <>
                        <p className="text-white leading-relaxed whitespace-pre-wrap tw-reading">{r.display_text || r.transcript}</p>
                        {r.display_text && <OriginalTranscript transcript={r.transcript} id={r.id} />}
                      </>
                    )}
                    {(() => {
                      const chain = buildThread(items, r.id);
                      if (chain.length <= 1) return null;
                      return (
                        <div className="mt-4 pt-3 border-t border-tw" data-testid={`submission-thread-${r.id}`}>
                          <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase mb-2 flex items-center gap-1.5">
                            <GitBranch size={11} strokeWidth={2.5} /> Gesprek
                          </div>
                          <ol className="space-y-2">
                            {chain.map((node) => (
                              <li key={node.id} data-testid={`submission-thread-node-${r.id}-${node.id}`}
                                className={`pl-3 border-l-2 ${node.id === r.id ? "border-tw-yellow" : "border-tw"}`}>
                                {node.question && <p className="text-xs italic text-tw-muted">&ldquo;{node.question}&rdquo;</p>}
                                <p className={`text-sm leading-snug ${node.id === r.id ? "text-white" : "text-tw-muted"}`}>
                                  {excerpt(node.display_text || node.transcript, 140)}
                                </p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      );
                    })()}
                    <ProposeEdit recordingId={r.id} currentText={r.display_text || r.transcript} />
                  </div>
                )}

                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    data-testid={`submission-open-${r.id}`}
                    className="tw-press flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-tw-yellow hover:text-white focus:outline-none focus-visible:text-white"
                  >
                    {isOpen ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />}
                    {isOpen ? "Sluit memo" : "Open memo"}
                  </button>
                  <Select
                    value=""
                    onValueChange={(v) => handleProcess(r.id, v)}
                    disabled={processingId === r.id}
                  >
                    <SelectTrigger
                      data-testid={`submission-process-${r.id}`}
                      className="tw-press h-7 px-2 w-auto gap-1.5 bg-transparent border-0 text-tw-muted hover:text-tw-yellow font-mono text-[11px] uppercase tracking-[0.2em] focus:ring-0 focus:ring-offset-0 focus:outline-none [&>svg]:hidden"
                    >
                      {processingId === r.id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 size={12} className="animate-spin" /> Verwerken…
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <Sparkles size={12} strokeWidth={2.5} />
                          {r.processed_text ? "Opnieuw verwerken…" : "Verwerk als…"}
                        </span>
                      )}
                    </SelectTrigger>
                    <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                      {TRANSFORM_ORDER.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`submission-process-opt-${r.id}-${t}`} className="focus:bg-tw-yellow focus:text-black rounded-none text-sm">
                          {TRANSFORM_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => navigate(`/?redo=${r.id}`)}
                    data-testid={`redo-submission-${r.id}`}
                    className="tw-press flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow focus:outline-none focus-visible:text-tw-yellow"
                    aria-label="Opnieuw inspreken"
                  >
                    <RotateCcw size={12} strokeWidth={2.5} />
                    Opnieuw inspreken
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(r.id)}
                    disabled={deletingId === r.id}
                    data-testid={`delete-submission-${r.id}`}
                    className="tw-press flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-red disabled:opacity-40 focus:outline-none focus-visible:text-tw-red"
                    aria-label="Opname verwijderen"
                  >
                    {deletingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={2.5} />}
                    Verwijder
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyState({ label, hint, testId }) {
  return (
    <div className="border border-dashed border-tw py-16 flex flex-col items-center text-center" data-testid={testId}>
      <Inbox size={40} className="text-tw-muted mb-4" />
      <div className="font-display text-2xl font-black text-white">{label}</div>
      <div className="text-sm text-tw-muted mt-2 max-w-sm">{hint}</div>
    </div>
  );
}
